import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidateLocalityCache, useAddLocality, useGetLocalitiesWithMinimalInfo } from 'src/api/localities'
import { ILocality } from 'src/generated-types'
import { handleLocalityErrorResponse } from 'src/screens/localities/utils'
import { ShowDialogProps } from 'src/show-dialog'
import { LocalityForm } from './LocalityForm'
import { Box, FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material'
import { useEffect, useState } from 'react'
import { AppForm, FormSelectField } from 'src/components/FormCore'
import { MainButton } from 'src/components/MainButton'
import { z } from 'zod'
import { useZodForm } from 'src/hooks/zod-form'
import { CreateLocalityOption } from 'src/enums'
import { CenteredSpinner } from 'src/components/Spinner'
import { GridActionButton } from 'src/components/common/GridActionButton'
import FormStepper from '../components/component/FormStepper'

interface CreateLocalitiesProps extends ShowDialogProps {
  customerId: EntityId
  withSteps?: boolean
  steps?: [{ label: string; completed: boolean; active: boolean }]
  onStepSuccess?: (customerId: EntityId, localityId: number, localityName?: string, locationNr?: string) => void
  onCancel?: (e) => void
  localityId?: number
}

const LocalityDialog: React.FC<CreateLocalitiesProps> = ({
  pop,
  customerId,
  withSteps = false,
  steps,
  onStepSuccess,
  onCancel,
  localityId,
}) => {
  const { t: translate } = useTranslation(['locality', 'common'])
  const { mutateAsync } = useAddLocality(customerId)
  const [selectedValue, setSelectedValue] = useState<CreateLocalityOption>(CreateLocalityOption.PickExistingLocality)

  const { data: localityList, isLoading } = useGetLocalitiesWithMinimalInfo(customerId as EntityId)

  const onSubmit = async (form: ILocality) => {
    await mutateAsync(form, {
      onSuccess: res => {
        toast.success(translate('addedSuccessfully', { ns: 'common' }))
        invalidateLocalityCache.getLocalities(customerId)
        invalidateLocalityCache.getLocalitiesWithMinimalInfo(customerId)
        pop()
        onStepSuccess && onStepSuccess(customerId, res.data.data, form.name, form.locationNr)
      },
      onError: (error: any) => {
        handleLocalityErrorResponse(error.data.message)
      },
    })
  }

  const pickLocalitySchema = z.object({
    localityId: z.number().min(1, translate('locality-required', { ns: 'projects' })),
    locality: z.any().optional(),
  })

  const form = useZodForm(pickLocalitySchema, {})

  const handleChange = (key, value) => {
    form.setValue(key, value)
    form.setValue(`${key}Id` as 'localityId', value?.id)
  }

  const onPickLocality = form => {
    onStepSuccess && onStepSuccess(customerId, form.localityId, form.locality?.name, form.locality?.locationNr)
    pop()
  }

  useEffect(() => {
    if (localityList?.items && localityList.items.length === 0) {
      setSelectedValue(CreateLocalityOption.CreateNewLocality)
    }
  }, [localityList?.items])

  useEffect(() => {
    if (localityList?.items && localityId) {
      setSelectedValue(CreateLocalityOption.PickExistingLocality)
      form.setValue('localityId', localityId)
      const selectedLocality = localityList.items.find(l => l.id === localityId)
      form.setValue('locality', selectedLocality)
    }
  }, [localityList?.items, form, localityId])

  return (
    <>
      {withSteps && steps && <FormStepper steps={steps} />}
      <Box p={1}>
        <FormControl>
          <RadioGroup
            aria-labelledby="demo-radio-buttons-group-label"
            defaultValue={CreateLocalityOption.PickExistingLocality}
            name="radio-buttons-locality"
            row
            value={selectedValue}
            onChange={e => setSelectedValue(e.target.value as CreateLocalityOption)}
          >
            {Object.values(CreateLocalityOption).map(option => (
              <FormControlLabel
                key={option}
                value={option}
                control={<Radio />}
                label={translate(option, { ns: 'locality' }) as string}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Box>
      {selectedValue === CreateLocalityOption.CreateNewLocality ? (
        <LocalityForm
          onSubmit={onSubmit}
          customerId={customerId as number}
          onStepback={e => onCancel && onCancel(e)}
          pop={pop}
        />
      ) : (
        <Box>
          <AppForm form={form}>
            {isLoading ? (
              <CenteredSpinner height={20} />
            ) : (
              <FormSelectField
                name="locality"
                label={translate('locality', { ns: 'locality' })}
                data={localityList?.items ?? []}
                getOptionLabel={option => option.name}
                renderOption={(props: any, option) => (
                  <Box key={option.id} {...props}>
                    <span style={{ textTransform: 'capitalize' }}>{option.name}</span>
                  </Box>
                )}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                onChange={(_, v) => handleChange('locality', v)}
                placeholder={translate('pick-existing-locality', { ns: 'locality' }) as string}
                defaultValue={localityList?.items.find(l => l.id === localityId)}
                sx={{ textTransform: 'capitalize' }}
              />
            )}
          </AppForm>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Box display={'flex'} gap={2}>
              <GridActionButton
                border={1}
                sx={{ width: '120px', height: '44px' }}
                onClick={e => {
                  onCancel && onCancel(e)
                  pop()
                }}
                buttonText={translate('back', { ns: 'common' })}
              />
              <MainButton
                key="project-form-button"
                variant="contained"
                onClick={() => {
                  form.handleSubmit(onPickLocality)()
                }}
                disabled={!form.watch('localityId')}
              >
                {translate('select-locality', { ns: 'locality' })}
              </MainButton>
            </Box>
          </Box>
        </Box>
      )}
    </>
  )
}

export default LocalityDialog
